import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { cx } from '../ui/cx';

export interface NavItem {
  label: string;
  to: string;
  icon?: ReactNode;
  /** Small count after the label (e.g. assigned issues). */
  badge?: ReactNode;
  children?: readonly Omit<NavItem, 'children'>[];
  /** Match only the exact path (for "/"). */
  end?: boolean;
}

/**
 * Sidebar menu (Ace skin-3 .nav-list). A <nav> landmark; the current page link gets
 * aria-current="page" from NavLink; sub-menus are disclosure buttons (aria-expanded). In
 * `collapsed` mode (icons only, Ace .menu-min) labels stay in the DOM as visually-hidden text
 * and as a tooltip, so links keep their accessible names.
 */
export function Sidebar({
  items,
  label = 'Main',
  collapsed = false,
  fluid = false,
  footer,
  className,
  onNavigate,
}: {
  items: readonly NavItem[];
  label?: string;
  collapsed?: boolean;
  /** Fill the container width (mobile drawer) instead of the fixed 190px. */
  fluid?: boolean;
  footer?: ReactNode;
  className?: string;
  /** Called after a link is followed (the mobile drawer closes itself). */
  onNavigate?: () => void;
}) {
  return (
    <nav
      aria-label={label}
      className={cx(
        'flex h-full flex-col border-e border-sidebar-border bg-sidebar',
        fluid ? 'w-full' : collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        className,
      )}
    >
      <ul className="flex-1 overflow-y-auto">
        {items.map((it) => (
          <SidebarEntry key={it.to} item={it} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </ul>
      {footer && <div className="border-t border-sidebar-border p-2">{footer}</div>}
    </nav>
  );
}

const linkClass = (active: boolean, collapsed: boolean, nested = false) =>
  cx(
    'relative flex min-h-nav-item items-center gap-2.5 border-b border-surface-sunken text-sm no-underline hover:no-underline',
    collapsed ? 'justify-center px-0' : nested ? 'ps-9 pe-3' : 'px-3',
    active
      ? 'bg-sidebar-active font-semibold text-sidebar-active-ink before:absolute before:inset-y-0 before:start-0 before:w-0.5 before:bg-sidebar-accent'
      : 'bg-sidebar-item text-sidebar-ink hover:bg-sidebar-item-hover hover:text-sidebar-ink-hover',
  );

function SidebarEntry({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const location = useLocation();
  const childActive = item.children?.some((c) => location.pathname.startsWith(c.to)) ?? false;
  const [open, setOpen] = useState(childActive);
  const subId = `sub${useId().replace(/:/g, '')}`;

  const inner = (
    <>
      {item.icon && (
        <span
          aria-hidden="true"
          className="inline-flex size-4 shrink-0 items-center justify-center"
        >
          {item.icon}
        </span>
      )}
      <span className={cx(collapsed ? 'sr-only' : 'flex-1 truncate')}>{item.label}</span>
      {item.badge !== undefined && item.badge !== null && !collapsed && (
        <span className="rounded-full bg-neutral px-1.5 text-xs text-ink-inverse">
          {item.badge}
        </span>
      )}
    </>
  );

  if (item.children?.length && !collapsed) {
    return (
      <li>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={subId}
          onClick={() => setOpen((o) => !o)}
          className={cx(
            linkClass(false, false),
            'w-full text-start',
            childActive && 'text-sidebar-active-ink',
          )}
        >
          {inner}
          <ChevronDown
            aria-hidden="true"
            className={cx('size-4 transition-transform', open && 'rotate-180')}
          />
        </button>
        <ul id={subId} hidden={!open}>
          {item.children.map((c) => (
            <li key={c.to}>
              <NavLink
                to={c.to}
                end={c.end ?? false}
                onClick={onNavigate}
                className={({ isActive }) => linkClass(isActive, false, true)}
              >
                <span className="flex-1 truncate">{c.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end ?? false}
        title={collapsed ? item.label : undefined}
        onClick={onNavigate}
        className={({ isActive }) => linkClass(isActive || childActive, collapsed)}
      >
        {inner}
      </NavLink>
    </li>
  );
}
